/**
 * Cliente SSE sobre fetch + ReadableStream (F14, streaming del análisis).
 *
 * EventSource del navegador solo soporta GET; el endpoint de análisis es
 * POST (lleva body con type=kickoff|message) y la auth va por cookie
 * HTTPOnly (DF6-03). Usamos fetch con credentials=include y parseamos
 * el wire format SSE a mano (es trivial: bloques separados por línea
 * vacía, con líneas `event:` y `data:`).
 *
 * El parser:
 *   - acumula líneas hasta encontrar una línea vacía (fin de evento)
 *   - extrae `event:` y concatena las líneas `data:` (puede haber varias)
 *   - ignora comentarios (líneas que empiezan con `:`, p.ej. keepalives)
 *
 * Cancelación: el caller pasa un AbortSignal; cuando se aborta, el
 * fetch se cancela y el iterador termina limpio.
 */

export type SseHandler = (event: string, data: unknown) => void;

export interface StreamSseOptions {
  url: string;
  body?: unknown;
  signal: AbortSignal;
  onEvent: SseHandler;
  onError?: (err: Error) => void;
}

export async function streamSse(opts: StreamSseOptions): Promise<void> {
  const { url, body, signal, onEvent, onError } = opts;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    // AbortError es esperado cuando el caller cancela; los demás van
    // al onError del consumer.
    if (signal.aborted) return;
    onError?.(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!response.ok || !response.body) {
    onError?.(
      new Error(
        `stream_http_error:${response.status}:${response.statusText || ""}`,
      ),
    );
    return;
  }

  const reader = response.body
    .pipeThrough(new TextDecoderStream())
    .getReader();

  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      // Procesar todos los bloques completos en el buffer.
      let blockEnd: number;
      while ((blockEnd = buffer.indexOf("\n\n")) >= 0) {
        const rawBlock = buffer.slice(0, blockEnd);
        buffer = buffer.slice(blockEnd + 2);
        parseAndDispatch(rawBlock, onEvent);
      }
    }
  } catch (err) {
    if (signal.aborted) return;
    onError?.(err instanceof Error ? err : new Error(String(err)));
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function parseAndDispatch(block: string, onEvent: SseHandler): void {
  if (!block.trim()) return;
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }
  if (dataLines.length === 0) return;
  const raw = dataLines.join("\n");
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = raw;
  }
  onEvent(eventName, parsed);
}
